import {
  absent,
  present,
  sameFile,
  ARCHIVE_ROOT,
  VaultError,
  type Attempt,
  type Conflict,
  type DocumentRecord,
  type FileChange,
  type FileState,
  type Guard,
  type Operation,
  type Recovery,
  type Snapshot,
  type VaultRecord,
  type VaultState,
  type Connection,
  type CheckpointResult,
  type MetadataPatch,
  type RepositoryMetadata,
  FOLDER_KEY_NAME,
  fileByteSize,
  isAgeFile,
  isEncryptedFile,
  isEncryptedNote,
  isFolderKey,
} from "./model";
import { defaultMetadata, validateCheckpointMinutes, validateIconPatch } from "./metadata";
import { MAX_CUSTOM_ICONS, customIconRef, isCustomIconId } from "../folder-icons";
import {
  assertDestination,
  notePath,
  relativePath,
  under,
  isNote,
  isMetadataPath,
  vaultFilePath,
  foldersFromPaths,
  encryptedFolderOf,
  folderOf,
  folderProblem,
  keyFolders,
  movedInto,
  noteExtension,
  moveProblem,
} from "./paths";
import { reconcile } from "./merge";
import { rebaseAttachmentImages } from "../markdown-image";
export const DATABASE_NAME = "turbonotes2-vault-v1";
const stores = [
  "vaults",
  "documents",
  "settings",
  "operations",
  "checkpointAttempts",
  "recovery",
] as const;
const request = <T>(r: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
export class StaleDraftError extends Error {
  constructor(public conflict: Conflict) {
    super("Another tab changed this note. Both drafts have been preserved.");
  }
}
export class GuardError extends Error {
  constructor() {
    super(
      "Vault changed while the request was in flight. Retry from the current vault.",
    );
  }
}
/** A merge of an encrypted note, worked out on its decrypted text before refreshing. */
export interface EncryptedMerge {
  path: string;
  local: string;
  remote: string;
  merged: string;
}
/** Plain text may never be stored where an age file belongs. */
function assertStorable(path: string, file: FileState): void {
  if (file.exists && isAgeFile(path)) fileByteSize(path, file.text);
}
export class LeaseBusyError extends Error {
  constructor(readonly expires: number) {
    super("GitHub sync is running in another tab.");
  }
}
export function guardOf(state: VaultState): Guard {
  const v = state.vault;
  return {
    generation: v.generation,
    epoch: v.epoch,
    fence: v.fence,
    baseCommitSha: v.baseCommitSha,
  };
}
export function pathRevision(state: VaultState, path: string): number {
  return (
    state.documents.find((d) => d.path === path)?.revision ??
    state.vault.pathRevisions[path] ??
    0
  );
}
export function filesOf(
  s: VaultState,
  base = false,
): Record<string, FileState> {
  const files: Record<string, FileState> = {};
  for (const doc of s.documents)
    files[doc.path] = base ? doc.base : present(doc.text);
  // Deleted documents still retain their base through the ordered operation log.
  if (base)
    for (const op of s.operations)
      for (const c of op.changes)
        if (!isMetadataPath(c.path) && !(c.path in files))
          files[c.path] = c.base ?? c.before;
  return files;
}
export function differences(
  base: Record<string, FileState>,
  files: Record<string, FileState>,
): FileChange[] {
  return [...new Set([...Object.keys(base), ...Object.keys(files)])]
    .sort()
    .filter((p) => !sameFile(base[p] ?? absent, files[p] ?? absent))
    .map((path) => ({
      path,
      before: base[path] ?? absent,
      after: files[path] ?? absent,
    }));
}
/** The first free Untitled name in a folder, without creating anything. */
export function untitledNotePath(state: VaultState, folder = ""): string {
  const files = state.documents.map((doc) => doc.path);
  const occupied = new Set([...files, ...foldersFromPaths(files, state.vault.repositoryFolders)]);
  const extension = noteExtension(folder, keyFolders(files));
  for (let number = 1; ; number++) {
    const path = notePath(`${folder ? `${folder}/` : ""}Untitled${number === 1 ? "" : ` ${number}`}${extension}`);
    if (!occupied.has(path)) return path;
  }
}
export class VaultDatabase {
  private db: Promise<IDBDatabase> | null = null;
  constructor(
    public readonly vaultId = "local",
    private readonly factory?: IDBFactory,
  ) {}
  private open(): Promise<IDBDatabase> {
    if (!this.db)
      this.db = new Promise((resolve, reject) => {
        const factory = this.factory ?? globalThis.indexedDB;
        if (!factory) {
          reject(
            new Error(
              "IndexedDB is unavailable. Keep editing and export your draft.",
            ),
          );
          this.db = null;
          return;
        }
        const r = factory.open(DATABASE_NAME, 1);
        r.onupgradeneeded = () => {
          const db = r.result;
          db.createObjectStore("vaults", { keyPath: "id" });
          db.createObjectStore("documents", { keyPath: ["vaultId", "path"] });
          db.createObjectStore("settings", { keyPath: "vaultId" });
          db.createObjectStore("operations", { keyPath: ["vaultId", "seq"] });
          db.createObjectStore("checkpointAttempts", {
            keyPath: ["vaultId", "id"],
          });
          db.createObjectStore("recovery", { keyPath: ["vaultId", "id"] });
        };
        r.onsuccess = () => {
          r.result.onversionchange = () => {
            r.result.close();
            this.db = null;
          };
          resolve(r.result);
        };
        r.onerror = () => {
          this.db = null;
          reject(r.error);
        };
        r.onblocked = () => {
          this.db = null;
          reject(new Error("Close older TurboNotes tabs to open storage."));
        };
      });
    return this.db.catch((error) => {
      this.db = null;
      throw error;
    });
  }
  private async transaction<T>(
    write: boolean,
    fn: (tx: IDBTransaction) => Promise<T>,
  ): Promise<T> {
    const db = await this.open(),
      tx = db.transaction([...stores], write ? "readwrite" : "readonly");
    const done = new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onabort = () =>
        reject(tx.error ?? new Error("Storage transaction aborted."));
      tx.onerror = () => {};
    });
    void done.catch(() => {});
    try {
      const value = await fn(tx);
      await done;
      return value;
    } catch (e) {
      try {
        tx.abort();
      } catch {}
      await done.catch(() => {});
      throw e;
    }
  }
  private async read(tx: IDBTransaction): Promise<VaultState> {
    const [v, docs, settings, operations, attempts, recovery] =
      await Promise.all([
        request(tx.objectStore("vaults").get(this.vaultId)),
        request(tx.objectStore("documents").getAll()),
        request(tx.objectStore("settings").get(this.vaultId)),
        request(tx.objectStore("operations").getAll()),
        request(tx.objectStore("checkpointAttempts").getAll()),
        request(tx.objectStore("recovery").getAll()),
      ]);
    const filter = <T extends { vaultId: string }>(items: T[]): T[] =>
      items.filter((x) => x.vaultId === this.vaultId);
    const now = Date.now();
    return {
      vault: v ?? {
        id: this.vaultId,
        generation: 0,
        sequence: 0,
        epoch: 0,
        fence: 0,
        lease: null,
        connection: null,
        baseCommitSha: null,
        lastCheckpoint: 0,
        createdAt: now,
        updatedAt: now,
        pathRevisions: {},
      },
      documents: filter<DocumentRecord>(docs),
      metadata: {
        vaultId: this.vaultId,
        // Settings stored before folder icons existed have none.
        value: { ...defaultMetadata(), ...settings?.value },
        pending: settings?.pending ?? {},
        repositories: settings?.repositories,
      },
      operations: filter<Operation>(operations),
      attempts: filter<Attempt>(attempts),
      recovery: filter<Recovery>(recovery),
    };
  }
  async load(): Promise<VaultState> {
    return this.transaction(false, (tx) => this.read(tx));
  }
  private async persist(
    tx: IDBTransaction,
    before: VaultState,
    after: VaultState,
  ): Promise<void> {
    after.vault.generation++;
    after.vault.updatedAt = Date.now();
    // Tombstone revisions outlive operation acknowledgement, preventing ABA
    // races when a stale tab creates a path that another tab created/deleted.
    for (const doc of before.documents) {
      if (!after.documents.some((d) => d.path === doc.path))
        after.vault.pathRevisions[doc.path] = ++after.vault.sequence;
    }
    for (const doc of after.documents)
      after.vault.pathRevisions[doc.path] = doc.revision;
    const put = async (name: string, value: unknown) =>
      request(tx.objectStore(name).put(value));
    const del = async (name: string, key: IDBValidKey) =>
      request(tx.objectStore(name).delete(key));
    await Promise.all([
      put("vaults", after.vault),
      put("settings", after.metadata),
      ...before.documents
        .filter((d) => !after.documents.some((n) => n.path === d.path))
        .map((d) => del("documents", [this.vaultId, d.path])),
      ...after.documents.map((d) => put("documents", d)),
      ...before.operations
        .filter((o) => !after.operations.some((n) => n.seq === o.seq))
        .map((o) => del("operations", [this.vaultId, o.seq])),
      ...after.operations.map((o) => put("operations", o)),
      ...after.attempts.map((a) => put("checkpointAttempts", a)),
      ...before.recovery
        .filter((r) => !after.recovery.some((n) => n.id === r.id))
        .map((r) => del("recovery", [this.vaultId, r.id])),
      ...after.recovery.map((r) => put("recovery", r)),
    ]);
  }
  async mutate<T>(fn: (s: VaultState) => T, guard?: Guard): Promise<T> {
    const run = () =>
      this.transaction(true, async (tx) => {
        const before = await this.read(tx);
        const s = structuredClone(before);
        if (
          guard &&
          Object.entries(guard).some(
            ([k, v]) => s.vault[k as keyof Guard] !== v,
          )
        )
          throw new GuardError();
        const result = fn(s);
        await this.persist(tx, before, s);
        return result;
      });
    return typeof navigator !== "undefined" && navigator.locks
      ? navigator.locks.request(`turbonotes2:${this.vaultId}`, run)
      : run();
  }
  private op(
    s: VaultState,
    kind: Operation["kind"],
    changes: FileChange[],
  ): void {
    if (changes.length) {
      const base = filesOf(s, true);
      const previous = s.operations.at(-1);
      const watermark = Math.max(
        0,
        ...s.attempts
          .filter((a) => a.state === "submitted")
          .map((a) => a.watermark),
      );
      if (
        changes.length === 1 &&
        previous?.changes.length === 1 &&
        previous.seq > watermark &&
        ["create", "update"].includes(kind) &&
        ["create", "update"].includes(previous.kind) &&
        previous.changes[0]!.path === changes[0]!.path
      ) {
        changes = [
          {
            ...changes[0]!,
            before: previous.changes[0]!.before,
            base: previous.changes[0]!.base,
          },
        ];
        kind = previous.kind;
        s.operations.pop();
      }
      s.operations.push({
        vaultId: this.vaultId,
        seq: ++s.vault.sequence,
        kind,
        changes: changes.map((c) => ({
          ...c,
          base: c.base ?? base[c.path] ?? absent,
        })),
      });
    }
  }
  private recover(
    s: VaultState,
    path: string,
    file: FileState,
    reason: string,
    conflict?: Conflict,
  ): void {
    s.recovery.push({
      vaultId: this.vaultId,
      id: crypto.randomUUID(),
      path,
      file,
      reason,
      createdAt: Date.now(),
      resolved: !conflict,
      ...(conflict ? { conflict } : {}),
    });
    // Keep every unresolved version and the newest snapshot of each affected path.
    const newest = new Map<string, string>();
    for (const r of s.recovery) newest.set(r.path, r.id);
    const removable = s.recovery.filter(
      (r) => r.resolved && newest.get(r.path) !== r.id,
    );
    const prune = new Set(
      removable.slice(0, Math.max(0, s.recovery.length - 100)).map((r) => r.id),
    );
    s.recovery = s.recovery.filter((r) => !prune.has(r.id));
  }
  async createUntitledNote(folder = ""): Promise<DocumentRecord> {
    return this.mutate((state) => this.insertUntitledNote(state, folder));
  }
  private insertUntitledNote(state: VaultState, folder = ""): DocumentRecord {
    const files = state.documents.map((doc) => doc.path);
    if (files.some((path) => path === folder || under(folder, path)))
      throw new VaultError("conflict", "Destination folder is occupied by a file.");
    const path = untitledNotePath(state, folder);
    // An encrypted note is never empty on disk; the store encrypts it first.
    if (isEncryptedNote(path))
      throw new VaultError("invalid_request", "Create encrypted notes through the store.");
    const base = filesOf(state, true)[path] ?? absent;
    const note: DocumentRecord = {
      vaultId: this.vaultId, path, text: "", archived: under(path, ARCHIVE_ROOT), base,
      dirty: !sameFile(base, present("")), revision: ++state.vault.sequence, editedAt: Date.now(),
    };
    state.documents.push(note);
    this.op(state, "create", [{ path, before: absent, after: present("") }]);
    return note;
  }
  async save(
    path: string,
    text: string,
    revision: number,
    original: FileState,
    editedAt = Date.now(),
  ): Promise<DocumentRecord> {
    const result = await this.mutate((s) => {
      vaultFilePath(path);
      assertStorable(path, present(text));
      const doc = s.documents.find((d) => d.path === path);
      if (pathRevision(s, path) !== revision) {
        const conflict: Conflict = {
          id: crypto.randomUUID(),
          paths: [path],
          reason: "A newer revision was saved by another tab.",
          base: { [path]: original },
          local: { [path]: present(text) },
          remote: { [path]: doc ? present(doc.text) : absent },
        };
        this.recover(s, path, present(text), "stale-draft", conflict);
        return conflict;
      }
      if (!doc)
        assertDestination(
          path,
          s.documents.map((d) => d.path),
        );
      const base = filesOf(s, true)[path] ?? absent;
      const next: DocumentRecord = {
        vaultId: this.vaultId,
        path,
        text,
        archived: under(path, ARCHIVE_ROOT),
        base,
        dirty: !sameFile(base, present(text)),
        revision: ++s.vault.sequence,
        editedAt,
      };
      s.documents = s.documents.filter((d) => d.path !== path);
      s.documents.push(next);
      this.op(s, doc ? "update" : "create", [
        {
          path,
          before: doc ? present(doc.text) : absent,
          after: present(text),
        },
      ]);
      return next;
    });
    if ("reason" in result) throw new StaleDraftError(result);
    return result;
  }
  /**
   * `text` replaces the stored text when the note is encrypted at its destination: its
   * image links were rebased on its plain text before it was encrypted.
   */
  async move(
    source: string,
    destination: string,
    revision: number,
    text?: string,
  ): Promise<void> {
    await this.mutate((s) => {
      notePath(destination);
      assertDestination(
        destination,
        s.documents.map((d) => d.path),
        source,
      );
      const doc = s.documents.find((d) => d.path === source);
      if (!doc || doc.revision !== revision) throw new GuardError();
      if (source === destination) return;
      const problem = moveProblem(source, destination, keyFolders(s.documents.map((d) => d.path)));
      if (problem) throw new VaultError("invalid_request", problem);
      const movedText = text ?? (isEncryptedFile(source) ? doc.text : rebaseAttachmentImages(doc.text, source, destination));
      assertStorable(destination, present(movedText));
      this.recover(s, source, present(doc.text), "pre-move");
      this.op(s, "move", [
        { path: source, before: present(doc.text), after: absent },
        { path: destination, before: absent, after: present(movedText) },
      ]);
      s.documents = s.documents.filter((d) => d.path !== source);
      s.documents.push({
        ...doc,
        path: destination,
        text: movedText,
        base: absent,
        dirty: true,
        archived: under(destination, ARCHIVE_ROOT),
        revision: ++s.vault.sequence,
        editedAt: Date.now(),
      });
      if (s.metadata.value.pinned.includes(source)) {
        this.updatePin(s, source, false);
        this.updatePin(s, destination, !under(destination, ARCHIVE_ROOT));
      }
    });
  }
  async delete(path: string, revision: number): Promise<void> {
    await this.mutate((s) => {
      const doc = s.documents.find((d) => d.path === path);
      if (!doc || doc.revision !== revision) throw new GuardError();
      this.recover(s, path, present(doc.text), "pre-delete");
      this.op(s, "delete", [
        { path, before: present(doc.text), after: absent },
      ]);
      s.documents = s.documents.filter((d) => d.path !== path);
      this.updatePin(s, path, false);
    });
  }
  private updatePin(s: VaultState, path: string, pinned: boolean): void {
    const pins = s.metadata.value.pinned;
    if (pins.includes(path) === pinned) return;
    s.metadata.value.pinned = pinned
      ? [...pins, path]
      : pins.filter((p) => p !== path);
    ((s.metadata.pending ??= {}).pins ??= {})[path] = pinned;
  }
  async applyMetadata(
    value: RepositoryMetadata,
    acknowledged?: MetadataPatch,
  ): Promise<void> {
    await this.mutate((s) => this.installMetadata(s, value, acknowledged));
  }
  private installMetadata(
    s: VaultState,
    value: RepositoryMetadata,
    acknowledged?: MetadataPatch,
  ): void {
    const pending = (s.metadata.pending ??= {});
    if (acknowledged?.auto_minutes === pending.auto_minutes)
      delete pending.auto_minutes;
    for (const [path, pinned] of Object.entries(acknowledged?.pins ?? {})) {
      if (pending.pins?.[path] === pinned) delete pending.pins[path];
    }
    for (const field of ["icons", "custom_icons"] as const)
      for (const [key, entry] of Object.entries(acknowledged?.[field] ?? {}))
        if (pending[field]?.[key] === entry) delete pending[field]![key];
    const pins = new Set(value.pinned);
    for (const [path, pinned] of Object.entries(pending.pins ?? {}))
      pinned ? pins.add(path) : pins.delete(path);
    // This tab's unsent icon changes stay on top of what the account holds.
    const withPending = (field: "icons" | "custom_icons") => {
      const entries = { ...(value[field] ?? {}) };
      for (const [key, entry] of Object.entries(pending[field] ?? {}))
        if (entry === null) delete entries[key];
        else entries[key] = entry;
      return entries;
    };
    s.metadata.value = {
      ...value,
      pinned: [...pins],
      checkpoint: {
        auto_minutes: pending.auto_minutes ?? value.checkpoint.auto_minutes,
      },
      icons: withPending("icons"),
      custom_icons: withPending("custom_icons"),
    };
  }
  private updateIcon(s: VaultState, folder: string, icon: string | null): void {
    const icons = s.metadata.value.icons;
    if ((icons[folder] ?? null) === icon) return;
    if (icon === null) delete icons[folder];
    else icons[folder] = icon;
    ((s.metadata.pending ??= {}).icons ??= {})[folder] = icon;
  }
  private updateUpload(s: VaultState, id: string, data: string | null): void {
    const uploads = s.metadata.value.custom_icons;
    if ((uploads[id] ?? null) === data) return;
    if (data === null) delete uploads[id];
    else uploads[id] = data;
    ((s.metadata.pending ??= {}).custom_icons ??= {})[id] = data;
  }
  /** Moves the icons of a folder and the folders in it, or forgets them when `destination` is null. */
  private moveIcons(s: VaultState, source: string, destination: string | null): void {
    for (const [folder, icon] of Object.entries(s.metadata.value.icons)) {
      if (folder !== source && !under(folder, source)) continue;
      this.updateIcon(s, folder, null);
      if (destination !== null) this.updateIcon(s, movedInto(folder, source, destination), icon);
    }
  }
  /**
   * Gives a folder an icon, or takes it away with null. `upload` is an uploaded icon to
   * keep first, as `icon` names it.
   */
  async setFolderIcon(
    folder: string,
    icon: string | null,
    upload?: { id: string; data: string },
  ): Promise<void> {
    await this.mutate((s) => {
      relativePath(folder);
      validateIconPatch({ [folder]: icon }, upload ? { [upload.id]: upload.data } : undefined);
      if (upload) {
        if (
          !(upload.id in s.metadata.value.custom_icons) &&
          Object.keys(s.metadata.value.custom_icons).length >= MAX_CUSTOM_ICONS
        )
          throw new VaultError(
            "invalid_request",
            `Keep up to ${MAX_CUSTOM_ICONS} uploaded icons. Forget one you no longer use first.`,
          );
        this.updateUpload(s, upload.id, upload.data);
      }
      this.updateIcon(s, folder, icon);
    });
  }
  /** Forgets an uploaded icon, and takes it off every folder that shows it. */
  async forgetUploadedIcon(id: string): Promise<void> {
    await this.mutate((s) => {
      if (!isCustomIconId(id)) throw new VaultError("invalid_request", "Invalid uploaded icon.");
      for (const [folder, icon] of Object.entries(s.metadata.value.icons))
        if (icon === customIconRef(id)) this.updateIcon(s, folder, null);
      this.updateUpload(s, id, null);
    });
  }
  /**
   * Renames a folder, moving everything the vault keeps in it as one change: notes,
   * images, its marker, and an encrypted folder's key. Encrypted files move as they are
   * stored, so a locked folder can be renamed too; plain notes have their image links
   * rebased. Returns the paths that moved, from where to where.
   */
  async moveFolder(source: string, destination: string): Promise<[string, string][]> {
    return this.mutate((s) => {
      relativePath(destination);
      const paths = s.documents.map((d) => d.path);
      const problem = folderProblem(source, destination, paths, foldersFromPaths(paths, s.vault.repositoryFolders));
      if (problem) throw new VaultError("invalid_request", problem);
      if (source === destination) return [];
      const moving = s.documents.filter((d) => under(d.path, source));
      if (!moving.length)
        throw new VaultError(
          "invalid_request",
          `“${source}” holds nothing TurboNotes keeps, so rename it on GitHub instead.`,
        );
      const moved = moving.map((doc) => {
        const path = movedInto(doc.path, source, destination);
        vaultFilePath(path);
        const text =
          isNote(doc.path) && !isEncryptedFile(doc.path)
            ? rebaseAttachmentImages(doc.text, doc.path, path)
            : doc.text;
        assertStorable(path, present(text));
        return { doc, path, text };
      });
      for (const { doc } of moved)
        if (isNote(doc.path)) this.recover(s, doc.path, present(doc.text), "pre-move");
      this.op(
        s,
        "move",
        moved.flatMap(({ doc, path, text }) => [
          { path: doc.path, before: present(doc.text), after: absent },
          { path, before: absent, after: present(text) },
        ]),
      );
      const leaving = new Set(moving.map((doc) => doc.path));
      s.documents = [
        ...s.documents.filter((doc) => !leaving.has(doc.path)),
        // Their edit times stay, so renaming a folder doesn't reorder recent notes.
        ...moved.map(({ doc, path, text }) => ({
          ...doc,
          path,
          text,
          base: absent,
          dirty: true,
          archived: under(path, ARCHIVE_ROOT),
          revision: ++s.vault.sequence,
        })),
      ];
      for (const { doc, path } of moved)
        if (s.metadata.value.pinned.includes(doc.path)) {
          this.updatePin(s, doc.path, false);
          this.updatePin(s, path, !under(path, ARCHIVE_ROOT));
        }
      this.moveIcons(s, source, destination);
      // GitHub's next snapshot brings the old folder back if it still holds other files.
      s.vault.repositoryFolders = s.vault.repositoryFolders?.filter(
        (folder) => folder !== source && !under(folder, source),
      );
      return moved.map(({ doc, path }): [string, string] => [doc.path, path]);
    });
  }
  /**
   * Deletes a folder and everything the vault keeps in it as one change, keeping a local
   * recovery copy of each note and key. Returns the paths deleted.
   */
  async deleteFolder(source: string): Promise<string[]> {
    return this.mutate((s) => {
      const paths = s.documents.map((d) => d.path);
      const problem = folderProblem(source, null, paths, foldersFromPaths(paths, s.vault.repositoryFolders));
      if (problem) throw new VaultError("invalid_request", problem);
      const deleting = s.documents.filter((d) => under(d.path, source));
      if (!deleting.length)
        throw new VaultError(
          "invalid_request",
          `“${source}” holds nothing TurboNotes keeps, so delete it on GitHub instead.`,
        );
      for (const doc of deleting)
        if (isNote(doc.path) || isFolderKey(doc.path))
          this.recover(s, doc.path, present(doc.text), "pre-delete");
      this.op(
        s,
        "delete",
        deleting.map((doc) => ({ path: doc.path, before: present(doc.text), after: absent })),
      );
      s.documents = s.documents.filter((d) => !under(d.path, source));
      for (const doc of deleting) this.updatePin(s, doc.path, false);
      this.moveIcons(s, source, null);
      s.vault.repositoryFolders = s.vault.repositoryFolders?.filter(
        (folder) => folder !== source && !under(folder, source),
      );
      return deleting.map((doc) => doc.path);
    });
  }
  async setAutoCheckpointMinutes(minutes: number): Promise<void> {
    await this.mutate((s) => {
      validateCheckpointMinutes(minutes);
      if (s.metadata.value.checkpoint.auto_minutes === minutes) return;
      s.metadata.value.checkpoint.auto_minutes = minutes;
      (s.metadata.pending ??= {}).auto_minutes = minutes;
    });
  }
  /**
   * Creates a folder from its marker file. With `key`, the marker is that folder key,
   * and the folder must be new: encrypting a folder never rewrites existing notes,
   * whose plain text would stay in Git history anyway.
   */
  async folder(path: string, key?: string): Promise<boolean> {
    relativePath(path);
    return this.mutate((s) => {
      const paths = s.documents.map((d) => d.path);
      const exists = foldersFromPaths(paths, s.vault.repositoryFolders).includes(path);
      if (key !== undefined) {
        if (exists)
          throw new VaultError("conflict", `“${path}” already exists. Only a new folder can be encrypted.`);
        if (under(path, ARCHIVE_ROOT) || path === ARCHIVE_ROOT)
          throw new VaultError("invalid_request", "An encrypted folder can't be inside the archive.");
        const outer = encryptedFolderOf(path, keyFolders(paths));
        if (outer !== null)
          throw new VaultError("invalid_request", `“${outer}” is already encrypted with its own key.`);
      } else if (exists) return false;
      const marker = `${path}/${key === undefined ? ".gitkeep" : FOLDER_KEY_NAME}`;
      const text = key ?? "";
      assertStorable(marker, present(text));
      assertDestination(marker, paths);
      const base = filesOf(s, true)[marker] ?? absent;
      s.documents.push({
        vaultId: this.vaultId,
        path: marker,
        text,
        base,
        archived: under(marker, ARCHIVE_ROOT),
        dirty: !sameFile(base, present(text)),
        revision: ++s.vault.sequence,
      });
      this.op(s, "create", [
        { path: marker, before: absent, after: present(text) },
      ]);
      return true;
    });
  }
  async pin(path: string, pin: boolean): Promise<void> {
    await this.mutate((s) => {
      notePath(path);
      this.updatePin(s, path, pin && !under(path, ARCHIVE_ROOT));
    });
  }
  async connect(connection: Connection | null, userId?: number): Promise<void> {
    await this.mutate((s) => {
      if (s.attempts.some((a) => a.state === "submitted"))
        throw new VaultError(
          "checkpoint_unknown",
          "Check the pending checkpoint before changing repositories.",
        );
      const key = (target: Connection, owner?: number) =>
        `${owner ?? "local"}:${target.repositoryId}:${target.branch}`;
      const previous = s.vault.connection;
      if (previous)
        (s.metadata.repositories ??= {})[key(previous, s.vault.userId)] = {
          value: structuredClone(s.metadata.value),
          pending: structuredClone(s.metadata.pending ?? {}),
        };
      if (
        previous &&
        (previous.repositoryId !== connection?.repositoryId ||
          previous.branch !== connection?.branch ||
          (s.vault.userId !== undefined &&
            userId !== undefined &&
            s.vault.userId !== userId))
      ) {
        const saved = connection
          ? s.metadata.repositories?.[key(connection, userId)]
          : undefined;
        s.metadata.value = saved?.value ?? defaultMetadata();
        s.metadata.pending = saved?.pending ?? {};
      }
      if (userId !== undefined) s.vault.userId = userId;
      s.vault.connection = connection;
      s.vault.epoch++;
      s.vault.fence++;
      s.vault.lease = null;
      s.vault.baseCommitSha = null;
      s.vault.remoteLoaded = false;
      s.vault.repositoryFolders = [];
      s.documents.forEach((d) => {
        d.base = absent;
        d.dirty = true;
      });
      s.operations = [];
      this.op(s, "update", differences({}, filesOf(s)));
    });
  }
  // browserManaged may only be used while holding this vault's sync Web Lock.
  async lease(owner: string, browserManaged = false): Promise<Guard> {
    return this.mutate((s) => {
      if (
        s.vault.lease &&
        s.vault.lease.expires > Date.now() &&
        s.vault.lease.owner !== owner &&
        !(browserManaged && s.vault.lease.browserManaged)
      )
        throw new LeaseBusyError(s.vault.lease.expires);
      s.vault.fence++;
      s.vault.lease = { owner, expires: Date.now() + 120000, browserManaged };
      return { ...guardOf(s), generation: s.vault.generation + 1 };
    });
  }
  async renewLease(owner: string, fence: number): Promise<void> {
    await this.mutate((s) => {
      if (s.vault.fence !== fence || s.vault.lease?.owner !== owner)
        throw new GuardError();
      s.vault.lease.expires = Date.now() + 120000;
    });
  }
  async release(owner: string, fence: number): Promise<void> {
    await this.mutate((s) => {
      if (s.vault.fence === fence && s.vault.lease?.owner === owner)
        s.vault.lease = null;
    });
  }
  async capture(
    message: string,
    guard?: Guard,
    onlyPaths?: readonly string[],
  ): Promise<Attempt> {
    return this.mutate((s) => {
      const pending = s.attempts.find((a) => a.state === "submitted");
      if (pending) return pending;
      if (!s.vault.connection)
        throw new VaultError("invalid_request", "Choose a repository first.");
      if (s.recovery.some((r) => !r.resolved))
        throw new VaultError("conflict", "Resolve every conflict first.");
      const selected = onlyPaths ? new Set(onlyPaths) : null;
      if (selected) {
        // A rename is one change: include both ends and every intermediate move.
        let size: number;
        do {
          size = selected.size;
          for (const operation of s.operations)
            if (
              operation.kind === "move" &&
              operation.changes.some((c) => selected.has(c.path))
            )
              operation.changes.forEach((c) => selected.add(c.path));
        } while (selected.size !== size);
      }
      const changes = differences(filesOf(s, true), filesOf(s)).filter(
        (c) => !selected || selected.has(c.path),
      );
      const attempt: Attempt = {
        vaultId: this.vaultId,
        id: crypto.randomUUID(),
        target: structuredClone(s.vault.connection),
        epoch: s.vault.epoch,
        baseCommitSha: s.vault.baseCommitSha,
        watermark: s.vault.sequence,
        message,
        changes,
        state: "submitted",
        createdAt: Date.now(),
      };
      s.attempts.push(attempt);
      return attempt;
    }, guard);
  }
  async rejectAttempt(id: string, guard: Guard): Promise<void> {
    await this.mutate((s) => {
      const a = s.attempts.find((a) => a.id === id);
      if (a?.state === "submitted") a.state = "rejected";
    }, guard);
  }
  async acknowledge(result: CheckpointResult, guard: Guard): Promise<void> {
    await this.mutate((s) => {
      const a = s.attempts.find((a) => a.id === result.attemptId);
      if (!a || a.state === "acknowledged") return;
      if (
        a.state !== "submitted" ||
        a.epoch !== s.vault.epoch ||
        s.vault.baseCommitSha !== a.baseCommitSha ||
        result.target.repositoryId !== a.target.repositoryId ||
        result.target.branch !== a.target.branch
      )
        throw new GuardError();
      result = {
        ...result,
        files: Object.fromEntries(
          Object.entries(result.files).filter(
            ([path]) => !isMetadataPath(path),
          ),
        ),
      };
      let local = filesOf(s);
      const previousBase = filesOf(s, true);
      // Preserve files discovered during the save; absence from the local
      // snapshot is not a staged deletion.
      for (const [path, file] of Object.entries(result.files))
        if (
          !(path in local) &&
          !(path in previousBase) &&
          !a.changes.some((c) => c.path === path)
        )
          local[path] = file;
      a.state = "acknowledged";
      s.vault.baseCommitSha = result.commitSha;
      s.vault.remoteLoaded = true;
      s.vault.lastCheckpoint = Date.now();
      s.vault.repositoryFolders =
        result.folders ??
        foldersFromPaths(
          Object.keys(result.files).filter((p) => result.files[p]!.exists),
        );
      const base = result.files;
      this.install(s, local, base);
      const moves = s.operations.filter(
        (o) =>
          o.kind === "move" &&
          (o.seq > a.watermark ||
            o.changes.some(
              (c) => !a.changes.some((change) => change.path === c.path),
            )) &&
          o.changes.some(
            (c) => !sameFile(local[c.path] ?? absent, base[c.path] ?? absent),
          ),
      );
      s.operations = moves.map((o) => ({
        ...o,
        changes: o.changes.map((c) => ({ ...c, base: base[c.path] ?? absent })),
      }));
      this.op(
        s,
        "update",
        differences(base, local).map((c) => ({ ...c, base: c.before })),
      );
    }, guard);
  }
  private install(
    s: VaultState,
    files: Record<string, FileState>,
    base: Record<string, FileState>,
  ): void {
    const docs: DocumentRecord[] = [];
    for (const [path, file] of Object.entries(files)) {
      if (isMetadataPath(path) || !file.exists) continue;
      vaultFilePath(path);
      const old = s.documents.find((d) => d.path === path);
      docs.push({
        vaultId: this.vaultId,
        path,
        text: file.text,
        archived: under(path, ARCHIVE_ROOT),
        base: base[path] ?? absent,
        dirty: !sameFile(file, base[path] ?? absent),
        revision:
          old && old.text === file.text ? old.revision : ++s.vault.sequence,
        editedAt: old && old.text === file.text ? old.editedAt : Date.now(),
      });
    }
    s.documents = docs;
  }
  async applySnapshot(
    snapshot: Snapshot,
    guard: Guard,
    choice: "merge" | "local" | "remote" = "merge",
    encrypted: EncryptedMerge[] = [],
  ): Promise<void> {
    await this.mutate((s) => {
      if (s.attempts.some((a) => a.state === "submitted"))
        throw new VaultError(
          "checkpoint_unknown",
          "Recover the submitted checkpoint first.",
        );
      if (s.recovery.some((r) => !r.resolved))
        throw new VaultError(
          "conflict",
          "Resolve existing conflicts before refreshing.",
        );
      // A pristine Untitled placeholder should not accompany an existing
      // repository. Recheck in the transaction so typing during fetch wins.
      const starter = s.documents[0];
      if (
        !s.vault.baseCommitSha &&
        choice === "merge" &&
        s.documents.length === 1 &&
        starter &&
        starter.text === "" &&
        !starter.base.exists &&
        /^Untitled(?: \d+)?\.md$/.test(starter.path) &&
        Object.keys(snapshot.files).some(isNote)
      ) {
        s.documents = [];
        s.operations = s.operations.filter(
          (op) => !op.changes.some((c) => c.path === starter.path),
        );
      }
      let local = filesOf(s);
      const base = filesOf(s, true);
      snapshot = {
        ...snapshot,
        files: Object.fromEntries(
          Object.entries(snapshot.files).filter(
            ([path]) => !isMetadataPath(path),
          ),
        ),
      };
      const resolved: Record<string, FileState> = {};
      for (const merge of encrypted) {
        // Worked out against these exact versions; anything newer means start again.
        const mine = local[merge.path],
          theirs = snapshot.files[merge.path];
        if (!mine?.exists || mine.text !== merge.local || !theirs?.exists || theirs.text !== merge.remote)
          throw new GuardError();
        resolved[merge.path] = present(merge.merged);
      }
      const merged =
        choice === "merge"
          ? reconcile(base, local, snapshot.files, s.operations, resolved)
          : {
              files: choice === "local" ? local : snapshot.files,
              conflicts: [],
            };
      for (const [path, file] of Object.entries(local))
        if (!sameFile(file, merged.files[path] ?? absent))
          this.recover(s, path, file, "pre-refresh");
      for (const conflict of merged.conflicts)
        for (const path of conflict.paths)
          this.recover(
            s,
            path,
            local[path] ?? absent,
            "remote-conflict",
            conflict,
          );
      this.install(s, merged.files, snapshot.files);
      s.vault.baseCommitSha = snapshot.commitSha;
      s.vault.remoteLoaded = true;
      s.vault.repositoryFolders =
        snapshot.folders ??
        foldersFromPaths(
          Object.keys(snapshot.files).filter((p) => snapshot.files[p]!.exists),
        );
      const moves = s.operations.filter(
        (o) =>
          o.kind === "move" &&
          o.changes.some(
            (c) =>
              !sameFile(
                merged.files[c.path] ?? absent,
                snapshot.files[c.path] ?? absent,
              ),
          ),
      );
      s.operations = moves.map((o) => ({
        ...o,
        changes: o.changes.map((c) => ({
          ...c,
          base: snapshot.files[c.path] ?? absent,
        })),
      }));
      this.op(
        s,
        "update",
        differences(snapshot.files, merged.files).map((c) => ({
          ...c,
          base: c.before,
        })),
      );
    }, guard);
  }
  async resolve(
    id: string,
    chosen: Record<string, FileState>,
    guard?: Guard,
    drafts: Record<string, FileState> = {},
  ): Promise<void> {
    await this.mutate((s) => {
      const records = s.recovery.filter(
        (r) => !r.resolved && r.conflict?.id === id,
      );
      const conflict = records[0]?.conflict;
      if (!conflict) throw new GuardError();
      for (const [path, file] of Object.entries(drafts))
        this.recover(s, path, file, "pre-conflict-draft");
      const files = filesOf(s);
      for (const [path, file] of Object.entries(chosen)) {
        vaultFilePath(path);
        assertStorable(path, file);
        if (!conflict.paths.includes(path) && file.exists)
          assertDestination(
            path,
            Object.keys(files).filter((p) => files[p]!.exists),
          );
      }
      for (const path of conflict.paths)
        if (!(path in chosen))
          throw new VaultError(
            "invalid_request",
            "Resolve every affected move path together.",
          );
      for (const [path, file] of Object.entries(chosen)) {
        this.recover(s, path, files[path] ?? absent, "pre-conflict");
        files[path] = file;
      }
      const paths = Object.keys(files).filter((p) => files[p]!.exists);
      for (const path of paths) assertDestination(path, paths, path);
      const base = filesOf(s, true);
      this.install(s, files, base);
      this.op(s, "update", differences(base, files));
      records.forEach((r) => (r.resolved = true));
    }, guard);
  }
  async restore(
    path: string,
    file: FileState,
    revision: number,
  ): Promise<void> {
    await this.mutate((s) => {
      notePath(path);
      assertStorable(path, file);
      const old = s.documents.find((d) => d.path === path);
      if (pathRevision(s, path) !== revision) throw new GuardError();
      if (!old && file.exists)
        assertDestination(
          path,
          s.documents.map((d) => d.path),
        );
      this.recover(s, path, old ? present(old.text) : absent, "pre-restore");
      const files = filesOf(s);
      files[path] = file;
      this.install(s, files, filesOf(s, true));
      this.op(s, "update", [
        { path, before: old ? present(old.text) : absent, after: file },
      ]);
    });
  }
}
